import {
  localizedSeoLocales,
  type LocalizedSeoLocale,
} from "./locale-strategy";
import { getSeoAwarePath } from "./locale-rollout";
import {
  getHomepageContent,
  getWave1HomeContent,
  type Wave1HomeContent,
} from "./wave1-home";

type NavigationContent = Wave1HomeContent["navigation"];
type FooterContent = Wave1HomeContent["footer"];

type ChromeNavigationLink = {
  href: string;
  label: string;
  description: string;
};

export interface LocalizedSiteChrome {
  navigation: NavigationContent;
  footer: FooterContent;
  blogLabel: string;
  examplesLabel: string;
  examplesNavigationLabel: string;
  comparisonsLabel: string;
  createVideoLabel: string;
  workflowLabel: string;
  workflowLinks: ChromeNavigationLink[];
  dialogueWorkflow: ChromeNavigationLink;
}

type ResourceLabels = {
  blog: string;
  examples: string;
  comparisons: string;
  createVideo: string;
  templates: string;
  calculator: string;
  pricing: string;
  about: string;
  editorial: string;
};

const resourceLabels: Record<LocalizedSeoLocale, ResourceLabels> = {
  pt: {
    blog: "Blog de vídeos sem rosto",
    examples: "Exemplos reais de vídeos",
    comparisons: "Comparações",
    createVideo: "Criar vídeo",
    templates: "Modelos gratuitos de roteiro",
    calculator: "Calculadora de duração",
    pricing: "Preços",
    about: "Sobre a AriaFlow",
    editorial: "Política editorial",
  },
  es: {
    blog: "Blog de vídeos sin rostro",
    examples: "Ejemplos reales de vídeos",
    comparisons: "Comparativas",
    createVideo: "Crear vídeo",
    templates: "Plantillas gratuitas de guion",
    calculator: "Calculadora de duración",
    pricing: "Precios",
    about: "Acerca de AriaFlow",
    editorial: "Política editorial",
  },
  de: {
    blog: "Blog für gesichtslose Videos",
    examples: "Echte Videobeispiele",
    comparisons: "Vergleiche",
    createVideo: "Video erstellen",
    templates: "Kostenlose Skriptvorlagen",
    calculator: "Videolängen-Rechner",
    pricing: "Preise",
    about: "Über AriaFlow",
    editorial: "Redaktionsrichtlinie",
  },
  fr: {
    blog: "Blog sur les vidéos sans visage",
    examples: "Exemples de vidéos réelles",
    comparisons: "Comparaisons",
    createVideo: "Créer une vidéo",
    templates: "Modèles de script gratuits",
    calculator: "Calculateur de durée",
    pricing: "Tarifs",
    about: "À propos d’AriaFlow",
    editorial: "Politique éditoriale",
  },
  ja: {
    blog: "顔出しなし動画ブログ",
    examples: "実際の動画サンプル",
    comparisons: "比較",
    createVideo: "動画を作成",
    templates: "無料台本テンプレート",
    calculator: "動画尺計算ツール",
    pricing: "料金",
    about: "AriaFlowについて",
    editorial: "編集方針",
  },
  zh: {
    blog: "Faceless 视频博客",
    examples: "真实视频样例",
    comparisons: "产品对比",
    createVideo: "创建视频",
    templates: "免费脚本模板",
    calculator: "视频时长计算器",
    pricing: "价格",
    about: "关于 AriaFlow",
    editorial: "编辑政策",
  },
  "zh-tw": {
    blog: "Faceless 影片部落格",
    examples: "真實影片範例",
    comparisons: "產品比較",
    createVideo: "建立影片",
    templates: "免費腳本範本",
    calculator: "影片長度計算器",
    pricing: "價格",
    about: "關於 AriaFlow",
    editorial: "編輯政策",
  },
  ko: {
    blog: "얼굴 없는 영상 블로그",
    examples: "실제 영상 샘플",
    comparisons: "제품 비교",
    createVideo: "동영상 만들기",
    templates: "무료 영상 대본 템플릿",
    calculator: "영상 길이 계산기",
    pricing: "가격",
    about: "AriaFlow 소개",
    editorial: "편집 정책",
  },
  nl: {
    blog: "Blog over gezichtsloze video’s",
    examples: "Echte videovoorbeelden",
    comparisons: "Vergelijkingen",
    createVideo: "Video maken",
    templates: "Gratis scriptsjablonen",
    calculator: "Videolengte berekenen",
    pricing: "Prijzen",
    about: "Over AriaFlow",
    editorial: "Redactioneel beleid",
  },
  pl: {
    blog: "Blog o filmach bez twarzy",
    examples: "Prawdziwe przykłady filmów",
    comparisons: "Porównania",
    createVideo: "Utwórz film",
    templates: "Bezpłatne szablony scenariuszy",
    calculator: "Kalkulator długości filmu",
    pricing: "Cennik",
    about: "O AriaFlow",
    editorial: "Polityka redakcyjna",
  },
  ru: {
    blog: "Блог о видео без лица",
    examples: "Примеры реальных видео",
    comparisons: "Сравнения",
    createVideo: "Создать видео",
    templates: "Бесплатные шаблоны сценариев",
    calculator: "Калькулятор длины видео",
    pricing: "Цены",
    about: "Об AriaFlow",
    editorial: "Редакционная политика",
  },
};

const workflowLabels: Record<LocalizedSeoLocale, string> = {
  pt: "Fluxos de criação",
  es: "Flujos de creación",
  de: "Workflows",
  fr: "Workflows",
  ja: "ワークフロー",
  zh: "工作流",
  "zh-tw": "工作流程",
  ko: "워크플로",
  nl: "Workflows",
  pl: "Przepływy pracy",
  ru: "Рабочие процессы",
};

const dialogueWorkflowCopy: Record<LocalizedSeoLocale, ChromeNavigationLink> = {
  pt: {
    href: "/dashboard/create-workflow/english-dialogue",
    label: "Diálogo em inglês por cenário",
    description:
      "Crie uma conversa curta em inglês com duas vozes e cartões de diálogo claros.",
  },
  es: {
    href: "/dashboard/create-workflow/english-dialogue",
    label: "Diálogo en inglés por escenas",
    description:
      "Crea una conversación breve en inglés con dos voces y tarjetas de diálogo claras.",
  },
  de: {
    href: "/dashboard/create-workflow/english-dialogue",
    label: "Englischer Szenendialog",
    description:
      "Erstelle einen kurzen englischen Dialog mit zwei Stimmen und klaren Dialogkarten.",
  },
  fr: {
    href: "/dashboard/create-workflow/english-dialogue",
    label: "Dialogue en situation en anglais",
    description:
      "Créez une courte conversation anglaise avec deux voix et des cartes de dialogue claires.",
  },
  ja: {
    href: "/dashboard/create-workflow/english-dialogue",
    label: "英語シーン会話",
    description:
      "二人分の音声と会話カードを使って、短い英会話動画を作成します。",
  },
  zh: {
    href: "/dashboard/create-workflow/english-dialogue",
    label: "英语场景对话",
    description: "用双角色独立音色和清晰对白卡片制作英语场景短视频。",
  },
  "zh-tw": {
    href: "/dashboard/create-workflow/english-dialogue",
    label: "英語情境對話",
    description: "用雙角色獨立音色和清楚對話卡片製作英語情境短影片。",
  },
  ko: {
    href: "/dashboard/create-workflow/english-dialogue",
    label: "영어 상황 대화",
    description:
      "두 개의 음성과 명확한 대화 카드로 짧은 영어 상황 영상을 만드세요.",
  },
  nl: {
    href: "/dashboard/create-workflow/english-dialogue",
    label: "Engelse situatiedialoog",
    description:
      "Maak een kort Engels gesprek met twee stemmen en duidelijke dialoogkaarten.",
  },
  pl: {
    href: "/dashboard/create-workflow/english-dialogue",
    label: "Dialog sytuacyjny po angielsku",
    description:
      "Utwórz krótką rozmowę po angielsku z dwoma głosami i czytelnymi kartami dialogowymi.",
  },
  ru: {
    href: "/dashboard/create-workflow/english-dialogue",
    label: "Ситуативный диалог на английском",
    description:
      "Создайте короткий диалог с двумя голосами и понятными карточками реплик.",
  },
};

const supplementalChrome: Record<
  "nl" | "pl" | "ru",
  Pick<LocalizedSiteChrome, "navigation" | "footer">
> = {
  nl: {
    navigation: {
      facelessVideo: "Gezichtsloze video",
      useCases: "Toepassingen",
      resources: "Bronnen",
      pricing: "Prijzen",
      dashboard: "Dashboard",
      signIn: "Inloggen",
      startFree: "Gratis starten",
      menu: "Menu",
      close: "Sluiten",
      useCaseLinks: [
        {
          href: "/faceless-youtube-video-generator",
          label: "YouTube-video’s",
          description: "Maak Shorts en langere kanaalvideo’s.",
        },
        {
          href: "/faceless-tiktok-video-generator",
          label: "TikTok-video’s",
          description: "Maak verticale video’s met leesbare ondertiteling.",
        },
        {
          href: "/faceless-instagram-reels-generator",
          label: "Instagram Reels",
          description: "Maak consistente Reels zonder presentator in beeld.",
        },
        {
          href: "/faceless-podcast-video-generator",
          label: "Podcastvideo’s",
          description: "Zet uitgeschreven gesprekken om in visuele afleveringen.",
        },
      ],
    },
    footer: {
      description:
        "Maak bewerkbare gezichtsloze video’s van een idee, script of URL en controleer ze vóór downloaden of publiceren.",
      productTitle: "Gezichtsloze video",
      useCasesTitle: "Toepassingen",
      resourcesTitle: "Bronnen",
      terms: "Voorwaarden",
      privacy: "Privacy",
      english: "Engels",
      productLinks: [
        ["/script-to-faceless-video", "Script naar video"],
        ["/url-to-faceless-video", "URL naar video"],
        ["/long-form-faceless-video-generator", "Lange video"],
        ["/faceless-video-automation", "Videoautomatisering"],
      ],
      useCaseLinks: [
        ["/faceless-youtube-video-generator", "YouTube-video’s"],
        ["/faceless-tiktok-video-generator", "TikTok-video’s"],
        ["/faceless-instagram-reels-generator", "Instagram Reels"],
        ["/faceless-podcast-video-generator", "Podcastvideo’s"],
      ],
      resourceLinks: [],
    },
  },
  pl: {
    navigation: {
      facelessVideo: "Filmy bez twarzy",
      useCases: "Zastosowania",
      resources: "Materiały",
      pricing: "Cennik",
      dashboard: "Panel",
      signIn: "Zaloguj się",
      startFree: "Zacznij bezpłatnie",
      menu: "Menu",
      close: "Zamknij",
      useCaseLinks: [
        {
          href: "/faceless-youtube-video-generator",
          label: "Filmy na YouTube",
          description: "Twórz Shorts i dłuższe filmy na kanał.",
        },
        {
          href: "/faceless-tiktok-video-generator",
          label: "Filmy na TikTok",
          description: "Twórz pionowe filmy z czytelnymi napisami.",
        },
        {
          href: "/faceless-instagram-reels-generator",
          label: "Instagram Reels",
          description: "Twórz spójne rolki bez pokazywania twarzy.",
        },
        {
          href: "/faceless-podcast-video-generator",
          label: "Filmy podcastowe",
          description: "Zmieniaj rozmowy ze scenariusza w wizualne odcinki.",
        },
      ],
    },
    footer: {
      description:
        "Twórz edytowalne filmy bez twarzy z pomysłu, scenariusza lub adresu URL i sprawdzaj je przed pobraniem lub publikacją.",
      productTitle: "Filmy bez twarzy",
      useCasesTitle: "Zastosowania",
      resourcesTitle: "Materiały",
      terms: "Warunki",
      privacy: "Prywatność",
      english: "angielski",
      productLinks: [
        ["/script-to-faceless-video", "Scenariusz na film"],
        ["/url-to-faceless-video", "URL na film"],
        ["/long-form-faceless-video-generator", "Długi film"],
        ["/faceless-video-automation", "Automatyzacja filmów"],
      ],
      useCaseLinks: [
        ["/faceless-youtube-video-generator", "Filmy na YouTube"],
        ["/faceless-tiktok-video-generator", "Filmy na TikTok"],
        ["/faceless-instagram-reels-generator", "Instagram Reels"],
        ["/faceless-podcast-video-generator", "Filmy podcastowe"],
      ],
      resourceLinks: [],
    },
  },
  ru: {
    navigation: {
      facelessVideo: "Видео без лица",
      useCases: "Сценарии использования",
      resources: "Материалы",
      pricing: "Цены",
      dashboard: "Панель",
      signIn: "Войти",
      startFree: "Начать бесплатно",
      menu: "Меню",
      close: "Закрыть",
      useCaseLinks: [
        {
          href: "/faceless-youtube-video-generator",
          label: "Видео для YouTube",
          description: "Создавайте Shorts и длинные видео для канала.",
        },
        {
          href: "/faceless-tiktok-video-generator",
          label: "Видео для TikTok",
          description: "Создавайте вертикальные видео с понятными субтитрами.",
        },
        {
          href: "/faceless-instagram-reels-generator",
          label: "Instagram Reels",
          description: "Создавайте единообразные Reels без ведущего в кадре.",
        },
        {
          href: "/faceless-podcast-video-generator",
          label: "Видео для подкастов",
          description: "Превращайте диалоги по сценарию в видеоэпизоды.",
        },
      ],
    },
    footer: {
      description:
        "Создавайте редактируемые видео без лица из идеи, сценария или URL и проверяйте их перед скачиванием или публикацией.",
      productTitle: "Видео без лица",
      useCasesTitle: "Сценарии использования",
      resourcesTitle: "Материалы",
      terms: "Условия",
      privacy: "Конфиденциальность",
      english: "английский",
      productLinks: [
        ["/script-to-faceless-video", "Сценарий в видео"],
        ["/url-to-faceless-video", "URL в видео"],
        ["/long-form-faceless-video-generator", "Длинное видео"],
        ["/faceless-video-automation", "Автоматизация видео"],
      ],
      useCaseLinks: [
        ["/faceless-youtube-video-generator", "Видео для YouTube"],
        ["/faceless-tiktok-video-generator", "Видео для TikTok"],
        ["/faceless-instagram-reels-generator", "Instagram Reels"],
        ["/faceless-podcast-video-generator", "Видео для подкастов"],
      ],
      resourceLinks: [],
    },
  },
};

const buildResourceLinks = (
  locale: LocalizedSeoLocale,
  englishLabel: string,
): Array<[string, string]> => {
  const labels = resourceLabels[locale];
  const hasLocalizedCoverMaker = locale === "zh" || locale === "zh-tw";
  const coverMakerPath = hasLocalizedCoverMaker
    ? `/${locale}/youtube-thumbnail-maker`
    : "/youtube-thumbnail-maker";
  const links: Array<[string, string, boolean?]> = [
    ["/blog", labels.blog],
    [coverMakerPath, "YouTube Thumbnail Maker", hasLocalizedCoverMaker],
    ["/examples", labels.examples],
    ["/compare", labels.comparisons],
    ["/resources/faceless-video-script-templates", labels.templates],
    ["/tools/faceless-video-length-calculator", labels.calculator],
    ["/pricing", labels.pricing],
    ["/about", labels.about],
    ["/editorial-policy", labels.editorial],
  ];

  return links.map(([path, label, isLocalized]) => [
    path,
    !isLocalized && getSeoAwarePath(path, locale) === path
      ? `${label} (${englishLabel})`
      : label,
  ]);
};

export const getLocalizedSiteChrome = (
  locale: string,
): LocalizedSiteChrome | undefined => {
  if (!localizedSeoLocales.includes(locale as LocalizedSeoLocale)) {
    return undefined;
  }

  const typedLocale = locale as LocalizedSeoLocale;
  const homepage = getWave1HomeContent(locale);
  const homepageContent = getHomepageContent(locale);
  const base =
    homepage ||
    supplementalChrome[typedLocale as keyof typeof supplementalChrome];
  if (!base) return undefined;

  const footer = {
    ...base.footer,
    resourceLinks: buildResourceLinks(typedLocale, base.footer.english),
  };
  const blogLabel = footer.resourceLinks.find(([path]) => path === "/blog")?.[1];
  const examplesLabel = footer.resourceLinks.find(([path]) => path === "/examples")?.[1];
  const comparisonsLabel = footer.resourceLinks.find(([path]) => path === "/compare")?.[1];
  const workflowLinks = homepageContent
    ? homepageContent.workflowTypes.items.map(({ href, title, description }) => ({
        href,
        label: title,
        description,
      }))
    : base.footer.productLinks.slice(0, 3).map(([href, label]) => ({
        href,
        label,
        description: "",
      }));

  return {
    navigation: base.navigation,
    footer,
    blogLabel: blogLabel || `${resourceLabels[typedLocale].blog} (${footer.english})`,
    examplesLabel:
      examplesLabel || `${resourceLabels[typedLocale].examples} (${footer.english})`,
    examplesNavigationLabel: resourceLabels[typedLocale].examples,
    comparisonsLabel:
      comparisonsLabel || `${resourceLabels[typedLocale].comparisons} (${footer.english})`,
    createVideoLabel: resourceLabels[typedLocale].createVideo,
    workflowLabel: workflowLabels[typedLocale],
    workflowLinks,
    dialogueWorkflow: dialogueWorkflowCopy[typedLocale],
  };
};
